import React, { useState, useEffect, useMemo } from 'react'
import Select, { components } from "react-select"
import { Progress } from 'reactstrap'
import RepAvatar from "../../Material/RepAvatar"
import { AllDriversQuery, CarsQuery, RepsQuery, AllBoardsQuery, GroupsQuery, AllIncidentsQuery, AllCarIssuesQuery } from "../Queries"
import { Query } from '@apollo/client/react/components'
import "./TypeAheadComponents.css"
import { useQuery } from '@apollo/client'

export const GroupsTypeAhead = ({ onChange, defaultValue }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [prevSelectedMap, setPrevSelectedMap] = useState({})

    useEffect(() => {
        if (defaultValue && Array.isArray(defaultValue)) {
            const map = {}
            defaultValue.forEach(item => {
                if (typeof item === "string" || typeof item === "number") {
                    map[String(item)] = prevSelectedMap[item] || String(item)
                } else if (item?.value && item?.label) {
                    map[String(item.value)] = item.label
                }
            })
            setPrevSelectedMap(map)
        }
    }, [defaultValue])

    return (
        <Query query={GroupsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading />
                if (error)
                    return (<Select isDisabledplaceholder={`Error! ${error.message}`} />)

                const options = data?.allGroups?.edges?.map((g) => ({
                    value: String(g.node.id),
                    label: g.node.name,
                })) || []

                if (!defaultValue) {
                    return (
                        <Select isMulti placeholder="Select Groups" options={options} defaultValue={undefined} onInputChange={(val) => setSearchTerm(val)} onChange={(sel) => onChange(sel ? sel.map((g) => g.value) : [])} />
                    )
                }

                const isIdArray = Array.isArray(defaultValue) && defaultValue.length > 0 && (typeof defaultValue[0] === "string" || typeof defaultValue[0] === "number")

                let controlledValue = null
                let uncontrolledDefault = null

                if (isIdArray) {
                    const normalizedIds = defaultValue.map(String)
                    const selectedInOptions = options.filter(opt => normalizedIds.includes(opt.value))
                    const missingSelected = normalizedIds.filter(id => !selectedInOptions.some(opt => opt.value === id)).map(id => ({ value: id, label: options.find(o => o.value === id)?.label || prevSelectedMap[id] || id }))
                    controlledValue = [...selectedInOptions, ...missingSelected]
                } else {
                    uncontrolledDefault = defaultValue.map(item => ({ value: String(item.value), label: item.label }))
                }
                return (
                    <Select
                        isMulti
                        placeholder="Select Groups"
                        options={options}
                        value={controlledValue}
                        defaultValue={uncontrolledDefault}
                        onInputChange={(val) => setSearchTerm(val)}
                        onChange={(sel) =>
                            onChange(sel ? sel.map((g) => g.value) : [])
                        }
                    />
                )
            }}
        </Query>
    )
}


export const BoardTypeAhead = ({ onChange, defaultValue, isDisabled = false }) => {
    const [searchTerm, setSearchTerm] = useState("")
    return (
        <Query query={AllBoardsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>
                let options = data && data.allBoards && data.allBoards.edges &&
                    data.allBoards.edges.map(board => ({
                        value: board.node.id,
                        label: board.node.title
                    }))

                let defaultOption = defaultValue && options?.find(option => option.value === defaultValue)
                return <Select
                    options={options}
                    isDisabled={isDisabled}
                    placeholder="Select Board"
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                    defaultValue={defaultOption}
                    key="boardTypeAhead" // defaultvalue Won't work without this https://github.com/JedWatson/react-select/issues/5888
                />
            }}
        </Query>
    )
}

export const DriverTypeAhead = ({ onChange, defaultValue, defaultLabelValue }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedDriver, setSelectedDriver] = useState(null)

    const { loading, error, data } = useQuery(AllDriversQuery, {
        variables: searchTerm ? { searchTerm } : {}
    })

    const options = useMemo(() => {
        return (
            data?.allDrivers?.edges?.map(driver => ({
                value: driver.node.id,
                label: `#${driver.node.pk} · ${driver.node.name} · ${driver.node.branch.name ?? ""}`
            })) || []
        )
    }, [data])

    // Resolve default selection AFTER options load
    useEffect(() => {
        if (selectedDriver) return

        if (defaultLabelValue) {
            setSelectedDriver(defaultLabelValue)
        } else if (defaultValue) {
            const match = options.find(o => o.value === defaultValue)
            if (match) setSelectedDriver(match)
        }
    }, [options, defaultValue, defaultLabelValue, selectedDriver])

    if (loading) return <Select isLoading />
    if (error)
        return (
            <Select
                isDisabled
                placeholder={`Error! ${error.message}`}
            />
        )

    return (
        <Select
            options={options}
            placeholder="Start typing to search for drivers..."
            value={selectedDriver}
            onInputChange={(inputValue, { action }) => {
                if (action === "input-change") {
                    setSearchTerm(inputValue)
                }
            }}
            onChange={(selected) => {
                setSelectedDriver(selected)
                onChange(selected.value)
            }}
        />
    )
}


export const CarTypeAhead = ({ onChange, defaultValue }) => {
    const [searchTerm, setSearchTerm] = useState("")
    return (
        <Query query={CarsQuery} variables={{ searchTerm }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>
                let options = data && data.allCars && data.allCars.edges &&
                    data.allCars.edges.map(
                        car => ({ value: car.node.id, label: `#${car.node.pk} · ${car.node.year} ${car.node.make} ${car.node.model} · ${car.node.vin}` })
                    )
                let defaultOption = defaultValue && options?.find(option => option.value === defaultValue)
                return <Select
                    options={options}
                    placeholder="Select Car"
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                    defaultValue={defaultOption}
                />
            }}
        </Query>
    )
}
export const IncidentTypeAhead = ({ onChange, defaultValue = null, value = null }) => {
    const [searchTerm, setSearchTerm] = useState("")
    return (
        <Query query={AllIncidentsQuery} variables={{ searchTerm, incidentId: value || null }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>

                let options = data?.allIncidents?.edges?.length > 0 ? data.allIncidents.edges.map(incident => ({ value: incident.node.id, label: `#${incident.node.pk} · ${incident.node.title}` })) : []
                let defaultOption = defaultValue && options?.find(option => option.value === defaultValue)
                if (value) {
                    defaultOption = options?.find(option => option.value === value)
                }
                return <Select
                    options={options}
                    isDisabled={value ? true : false}
                    placeholder="Select Incident"
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                    defaultValue={defaultOption}
                    key={defaultOption ? "incidentTypeAhead" : undefined}
                />
            }}
        </Query>
    )
}
export const CarIssuesTypeAhead = ({ onChange, defaultValue = null, value = null }) => {
    const [searchTerm, setSearchTerm] = useState("")
    return (
        <Query query={AllCarIssuesQuery} variables={{ searchTerm, carIssueId: value || null }}>
            {({ loading, error, data }) => {
                if (loading) return <Select isLoading={true}></Select>
                if (error) return <Select isLoading={false} isDisabled={true} placeholder={`Error! ${error.message}`}></Select>

                let options = data?.allCarIssues?.edges?.length > 0 ? data.allCarIssues.edges.map(carIssue => ({ value: carIssue.node.id, label: `#${carIssue.node.pk} · ${carIssue.node.issueCategory?.category || "No Category Selected"} (${carIssue.node.car?.year} ${carIssue.node.car?.make} ${carIssue.node.car?.model} #${carIssue.node.car?.pk})` })) : []
                let defaultOption = defaultValue && options?.find(option => option.value === defaultValue)
                if (value) {
                    defaultOption = options?.find(option => option.value === value)
                }
                return <Select
                    options={options}
                    isDisabled={value ? true : false}
                    placeholder="Select Car Issue"
                    onInputChange={(inputValue) => { setSearchTerm(inputValue) }}
                    onChange={(selected) => onChange(selected.value)}
                    defaultValue={defaultOption}
                    key={defaultOption ? "carIssuesTypeAhead" : undefined}
                />
            }}
        </Query>
    )
}

const RepOption = (props) => (
    <components.Option {...props}>
        <div className="d-flex align-items-center">
            <RepAvatar rep={props.data.node} keepClose />
            <span className="ml-2">{props.label}</span>
        </div>
    </components.Option>
)

export const RepsTypeAhead = ({ onChange, defaultValue = [], group, permissions }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [selectedReps, setSelectedReps] = useState(defaultValue || [])
    const [queryVariables, setQueryVariables] = useState({ limit: 5, searchTerm: "", groups: group, permissions: permissions })
    const [loadedData, setLoadedData] = useState(null)
    const isArrayOfStrings = Array.isArray(defaultValue) && defaultValue.every(id => typeof id === "string")

    useEffect(() => {
        setQueryVariables({ limit: 5, searchTerm: "", groups: group, permissions: permissions })
    }, [])

    useEffect(() => {
        if (!isArrayOfStrings) return
        if (!loadedData?.allReps?.edges || defaultValue.length === 0) return

        const repObjects = defaultValue
            .map(id => loadedData.allReps.edges.find(e => e.node.id === id)?.node)
            .filter(Boolean)

        setSelectedReps(repObjects)
    }, [defaultValue, loadedData, isArrayOfStrings])

    const handleSelectChange = (node) => {
        if (!node) return
        if (!selectedReps.some(r => r.id === node.id)) {
            const updated = [...selectedReps, node]
            setSelectedReps(updated)
            onChange(updated.map(r => r.id))
        }
    }

    const handleRemoveRep = (repId) => {
        setSelectedReps(prevSelected => {
            const updated = prevSelected.filter(r => r.id !== repId)
            onChange(updated.length > 0 ? updated.map(r => r.id) : [])
            return updated
        })
    }

    const handleSearch = (inputValue) => {
        setSearchTerm(inputValue)
        setQueryVariables({
            limit: 5,
            searchTerm: inputValue && inputValue.trim().length > 0 ? inputValue.trim() : "",
            groups: group,
            permissions: permissions
        })
    }

    return (
        <div className="reps-typeahead-container">
            {selectedReps.length > 0 && (
                <div className="selected-reps">
                    {selectedReps.map((rep) => (
                        <div key={rep.pk} className="selected-rep-chip">
                            <RepAvatar rep={rep} keepOpen />
                            <i className="fa fa-times remove-rep" onClick={() => handleRemoveRep(rep.id)} />
                        </div>
                    ))}
                </div>
            )}

            <Query query={RepsQuery} variables={queryVariables}>
                {({ error, data, loading }) => {
                    if (data && data !== loadedData) {
                        setLoadedData(data)
                    }
                    if (error)
                        return (
                            <div className="text-danger text-center py-2">Error loading reps: {error.message}</div>
                        )
                    let allReps = data?.allReps?.edges?.map(rep => ({
                        value: rep.node.id, label: rep.node.name || rep.node.username || `${rep.node.firstName || ""} ${rep.node.lastName || ""}`.trim(), node: rep.node,
                    })) || []

                    const selectedIds = selectedReps.map(r => r.value)
                    allReps = [...allReps.filter(r => selectedIds.includes(r.value)), ...allReps.filter(r => !selectedIds.includes(r.value))]

                    return (
                        <Select options={allReps} isLoading={loading} components={{ Option: RepOption }} placeholder="Search and select reps..." onInputChange={handleSearch} onChange={(selected) => handleSelectChange(data?.allReps?.edges?.find(r => r.node.id === selected.value)?.node)} isClearable={false} value={null} noOptionsMessage={() => searchTerm ? "No reps found for this search." : "No reps available."} />
                    )
                }}
            </Query>
        </div>
    )
}
